"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { MessageCircleHeart, X } from "lucide-react";
import { localePath } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { cn } from "@/lib/utils";
export type CareDockProps = {
  onDismiss: () => void;
  onCta: (kind: "error" | "suggestion") => void;
  className?: string;
  /** Salida animada (la provee CareDockHost vía useAnimatedOpen). */
  leaving?: boolean;
};

/**
 * Floating liquid-glass care prompt: support + suggestions after tool success.
 * Non-modal — does not steal focus.
 */
export function CareDock(t0) {
  const $ = _c(65);
  const {
    onDismiss,
    onCta,
    className,
    leaving: t1
  } = t0;
  const leaving = t1 === undefined ? false : t1;
  const t = useT();
  const locale = useLocale();
  let t2;
  if ($[0] !== locale) {
    t2 = path => localePath(locale, path);
    $[0] = locale;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const lp = t2;
  let t3;
  if ($[2] !== className) {
    t3 = cn("pointer-events-none fixed inset-x-0 bottom-0 z-[80] flex justify-center p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("careDockRegionLabel");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = leaving ? "animate-out fade-out slide-out-to-bottom-1 duration-150 motion-reduce:animate-none" : "animate-in fade-in slide-in-from-bottom-1 duration-150 motion-reduce:animate-none";
  let t6;
  if ($[6] !== t5) {
    t6 = cn("care-dock-panel pointer-events-auto relative w-full max-w-[28rem] overflow-hidden", "rounded-[var(--radius-card)] border border-[var(--border-strong)]", "bg-[rgba(14,14,20,0.72)] shadow-[var(--shadow-raised)]", "backdrop-blur-sm backdrop-saturate-150", "supports-[backdrop-filter]:bg-[rgba(14,14,20,0.55)]", t5);
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent" aria-hidden="true" />;
    t8 = <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(500px_circle_at_20%_0%,rgba(255,255,255,0.12),transparent_45%)]" aria-hidden="true" />;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-[var(--border-strong)] bg-[var(--accent-soft)]" aria-hidden="true"><MessageCircleHeart className="size-4 text-[var(--accent)]" /></div>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t) {
    t10 = t("careDockTitle");
    $[11] = t;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10) {
    t11 = <p id="care-dock-title" className="text-[var(--step--1)] font-semibold leading-snug text-foreground">{t10}</p>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t) {
    t12 = t("careDockBody");
    $[15] = t;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12) {
    t13 = <p className="mt-0.5 text-[var(--step--2)] leading-relaxed text-muted-foreground">{t12}</p>;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== lp) {
    t14 = lp("/contact");
    $[19] = lp;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== onCta) {
    t15 = () => onCta("error");
    $[21] = onCta;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn("inline-flex min-h-9 items-center justify-center rounded-full border border-[var(--border-strong)]", "bg-transparent px-3.5 text-[var(--step--2)] font-medium text-foreground-soft", "transition-[transform,background-color,border-color] duration-200 ease-[var(--ease-enter)]", "hover:border-[var(--border-strong)] hover:bg-[var(--muted)] hover:text-foreground", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35", "active:scale-[0.98]");
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t) {
    t17 = t("careDockReportError");
    $[24] = t;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t14 || $[27] !== t15 || $[28] !== t17) {
    t18 = <Link href={t14} data-testid="care-dock-report" onClick={t15} className={t16}>{t17}</Link>;
    $[26] = t14;
    $[27] = t15;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== lp) {
    t19 = lp("/suggestions");
    $[30] = lp;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== onCta) {
    t20 = () => onCta("suggestion");
    $[32] = onCta;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = cn("inline-flex min-h-9 items-center justify-center rounded-full", "bg-[var(--accent)] px-3.5 text-[var(--step--2)] font-semibold text-[var(--accent-foreground)]", "transition-[transform,opacity] duration-200 ease-[var(--ease-enter)]", "hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/40", "active:scale-[0.98]");
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t) {
    t22 = t("careDockSuggest");
    $[35] = t;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t19 || $[38] !== t20 || $[39] !== t22) {
    t23 = <Link href={t19} data-testid="care-dock-suggest" onClick={t20} className={t21}>{t22}</Link>;
    $[37] = t19;
    $[38] = t20;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t18 || $[42] !== t23) {
    t24 = <div className="mt-3 flex flex-wrap gap-2">{t18}{t23}</div>;
    $[41] = t18;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t11 || $[45] !== t13 || $[46] !== t24) {
    t25 = <div className="min-w-0 flex-1">{t11}{t13}{t24}</div>;
    $[44] = t11;
    $[45] = t13;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = cn("inline-flex size-9 shrink-0 items-center justify-center rounded-full", "text-muted-foreground transition-colors duration-150", "hover:bg-[var(--muted)] hover:text-foreground", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35");
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t) {
    t27 = t("careDockClose");
    $[49] = t;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <X className="size-3.5" aria-hidden="true" />;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== onDismiss || $[53] !== t27) {
    t29 = <button type="button" data-testid="care-dock-close" onClick={onDismiss} className={t26} aria-label={t27}>{t28}</button>;
    $[52] = onDismiss;
    $[53] = t27;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== t25 || $[56] !== t29) {
    t30 = <div className="relative flex items-start gap-3 p-3.5 sm:p-4">{t9}{t25}{t29}</div>;
    $[55] = t25;
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== t30 || $[59] !== t4 || $[60] !== t6) {
    t31 = <div role="region" aria-labelledby="care-dock-title" aria-label={t4} data-testid="care-dock" className={t6}>{t7}{t8}{t30}</div>;
    $[58] = t30;
    $[59] = t4;
    $[60] = t6;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== t3 || $[63] !== t31) {
    t32 = <div className={t3} data-testid="care-dock-root">{t31}</div>;
    $[62] = t3;
    $[63] = t31;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  return t32;
}